import type { ApiError, ApiErrorCode } from "../shared/api";

export class ApiFailure extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ApiErrorCode,
    public readonly userMessage?: string,
    public readonly retryAfter?: number,
  ) {
    super(code);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  timeoutMs = 20_000,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      "X-Requested-With": "fetch",
      ...(body !== undefined && { "Content-Type": "application/json" }),
    },
    credentials: "same-origin",
    signal: AbortSignal.timeout(timeoutMs),
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(`/api${path}`, init);
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as T & Partial<ApiError>;
  if (!res.ok) {
    // A proxy or a crash may answer null or a string: read it as an unknown error.
    const e = (data && typeof data === "object" ? data : {}) as Partial<ApiError>;
    throw new ApiFailure(res.status, e.error ?? "INTERNAL", e.message, e.retryAfter);
  }
  return data;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}, timeoutMs?: number) =>
    request<T>("POST", path, body, timeoutMs),
};
